// ─── Modale : rattacher des opérations à une prévision ────────────
// Spécification du 12/08. Sélection MULTIPLE avec recherche, tri et six
// filtres ; chaque opération peut être partagée entre plusieurs prévisions.
//
// Trois règles que l'écran doit rendre visibles :
//   • rien n'est écrit avant « Sauver » — la sélection est un brouillon ;
//   • une opération déjà rattachée ailleurs reste sélectionnable, et l'écran
//     annonce la part qu'elle prendrait ET ce que les autres perdraient ;
//   • une opération hors plage n'est pas masquée : elle porte un chip orange
//     et bloque la sauvegarde tant qu'un mois n'a pas été choisi (§9.1).
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet } from 'react-native';
// ⚠️ Les sous-panneaux (tri, mois) sont des CALQUES, pas des <Feuille> : deux
// <Modal> natives imbriquées figent l'écran. Seule la modale principale est
// une Feuille — piège documenté dans le Core et dans COWORK.md.
import { Feuille, CalqueChoix, useUI, useT, frJMA, confirmerChoix } from '@mop/core';
import { formatEuro, montantOperation, dateEffective } from './services/calculs';
import { calculerPart, pourcentage, rattachementsDeDepense } from './services/repartition';
import {
  FILTRES_DEFAUT, filtrerCandidates, trierCandidates, TRIS_RATTACHEMENT,
  selectionInitiale, diffSelection, moisParDefaut, horsPlage,
} from './services/selectionOperations';
import FeuilleFiltresRattachement from './FeuilleFiltresRattachement';
import BarreRechercheRattachement from './BarreRechercheRattachement';

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

export default function FeuilleRattacherOperations({
  visible, ligne, mois, moisDus = [], reference = 0,
  operations = [], rattachements = [], lignes = [], comptes = [],
  onValider, onFermer,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);

  const sensDefaut = ligne?.sens === 'revenu' ? 'revenu' : 'depense';
  const [filtres, setFiltres] = useState({ ...FILTRES_DEFAUT, sens: sensDefaut });
  const [recherche, setRecherche] = useState('');
  const [tri, setTri] = useState({ tri: 'date', sens: -1 });
  const [panneau, setPanneau] = useState(null);        // 'filtres' | 'tri'
  const [choisies, setChoisies] = useState(new Set());
  const [moisChoisi, setMoisChoisi] = useState({});     // depenseId → 'AAAA-MM'

  const initiale = useMemo(() => selectionInitiale(rattachements, ligne?.id), [rattachements, ligne]);

  // À l'ouverture : les opérations déjà rattachées arrivent COCHÉES (§8), et
  // leur mois est celui du rattachement — jamais recalculé (§9.2).
  useEffect(() => {
    if (!visible) return;
    setChoisies(new Set(initiale));
    const m = {};
    rattachements.filter(r => r?.ligneId === ligne?.id).forEach(r => { m[r.depenseId] = r.mois; });
    setMoisChoisi(m);
    setFiltres({ ...FILTRES_DEFAUT, sens: sensDefaut });
    setRecherche('');
  }, [visible, initiale, rattachements, ligne, sensDefaut]);

  const liste = useMemo(() => trierCandidates(
    filtrerCandidates({ operations, rattachements, ligneId: ligne?.id, filtres, recherche, reference }),
    { ...tri, comptes },
  ), [operations, rattachements, ligne, filtres, recherche, reference, tri, comptes]);

  const diff = useMemo(() => diffSelection(initiale, choisies), [initiale, choisies]);
  const nomLigne = (id) => (lignes.find(l => l.id === id) || {}).libelle || '';
  const moisDe = (op) => moisChoisi[op.id] || moisParDefaut(op);

  // La part que CETTE prévision prendrait, et ce que les autres perdraient.
  const partDe = (op) => calculerPart(op, rattachements, ligne?.id);

  // §3 — sous-titre : nombre choisi et somme des parts effectives.
  const somme = useMemo(() => liste
    .filter(o => choisies.has(o.id))
    .reduce((n, o) => n + (initiale.has(o.id)
      ? Math.abs((rattachementsDeDepense(rattachements, o.id).find(r => r.ligneId === ligne?.id) || {}).montant || 0)
      : partDe(o).montant), 0), [liste, choisies, initiale, rattachements, ligne]);

  // §9.1 — hors plage : sélectionnée mais sans mois explicite → Sauver bloqué.
  const enAttenteDeMois = useMemo(() => liste.filter(o =>
    choisies.has(o.id) && horsPlage(o, moisDus) && !moisChoisi[o.id]), [liste, choisies, moisDus, moisChoisi]);

  const basculer = (op) => {
    const p = partDe(op);
    if (!choisies.has(op.id) && !p.possible) return;   // §15 : part < 0,01 refusée
    setChoisies(prev => {
      const n = new Set(prev);
      if (n.has(op.id)) n.delete(op.id); else n.add(op.id);
      return n;
    });
  };

  const fermer = () => {
    if (!diff.modifie) return onFermer?.();
    confirmerChoix(t('finances.abandonner'), '', [
      // `text` et non `texte` : contrat de `Alert.alert`. Cette confirmation
      // affichait deux boutons VIDES depuis le 12/08 — personne ne l'avait vu,
      // parce qu'il faut abandonner une selection en cours pour la declencher.
      { text: t('commun.annuler'), style: 'cancel' },
      { text: t('commun.valider'), onPress: () => onFermer?.() },
    ]);
  };

  return (
    // `sansScroll` : la Feuille ne doit PAS envelopper ce contenu dans son
    // propre ScrollView. Sans ça, le compteur et la barre recherche/filtre/tri
    // défilent avec la liste et sortent de l'écran dès qu'elle s'allonge — il
    // faut alors remonter tout en haut pour rouvrir un filtre (signalé par
    // Cédric le 12/08). Ici seule la LISTE défile ; les commandes sont fixes.
    // Contrepartie : le `padding: 16` du corps disparaît, chaque bloc porte
    // désormais sa propre marge horizontale.
    <Feuille visible={visible} titre={t('finances.rattacherOps')} emoji="🔗" hauteur={0.95}
      sansScroll onFermer={fermer}
      action={{ icone: '✓', label: t('commun.enregistrer'),
        disabled: !diff.modifie || enAttenteDeMois.length > 0,
        onPress: () => onValider?.({ diff, moisChoisi, liste }) }}>

      <Text style={s.entete}>
        {t('finances.selectionnees', { n: choisies.size })}  ·  <Text style={s.somme}>{formatEuro(somme)}</Text>
      </Text>

      {/* Barre PARTAGEE avec la feuille de rattachement simple : deux copies
          avaient deja diverge ailleurs (le vocabulaire des statuts). */}
      <BarreRechercheRattachement style={s.barre}
        recherche={recherche} onRecherche={setRecherche}
        filtres={filtres} sensDefaut={sensDefaut} triActif={tri.tri !== 'date'}
        onOuvrirFiltres={() => setPanneau('filtres')}
        onOuvrirTri={() => setPanneau('tri')} />

      {/* Le padding latéral va sur le CONTENU, pas sur le conteneur : sinon la
          barre de défilement se décolle du bord de la feuille. */}
      <ScrollView style={{ flex: 1 }}
        contentContainerStyle={{ paddingBottom: 26, paddingHorizontal: 16 }}>
        {liste.length === 0 ? (
          <View style={s.vide}>
            <Text style={s.videTxt}>{t('finances.aucuneApresFiltre')}</Text>
            <TouchableOpacity onPress={() => { setFiltres({ ...FILTRES_DEFAUT, sens: sensDefaut }); setRecherche(''); }}>
              <Text style={s.videLien}>↺ {t('finances.reinitFiltres')}</Text>
            </TouchableOpacity>
          </View>
        ) : liste.map(o => {
          const on = choisies.has(o.id);
          const autres = rattachementsDeDepense(rattachements, o.id).filter(r => r.ligneId !== ligne?.id);
          const p = partDe(o);
          const hors = horsPlage(o, moisDus);
          return (
            <TouchableOpacity key={o.id} style={[s.op, on && s.opOn]} activeOpacity={0.7}
              onPress={() => basculer(o)}>
              <Text style={s.coche}>{on ? '☑' : '☐'}</Text>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={[s.opLib, on && s.opLibOn]} numberOfLines={1}>
                  {o.libelle || '—'}
                </Text>
                <Text style={s.opSous} numberOfLines={1}>
                  {frJMA(dateEffective(o))}
                  {o.compteId ? `  ·  ${(comptes.find(c => c.id === o.compteId) || {}).nom || ''}` : ''}
                </Text>
                {/* §8 — ce que d'autres prévisions prennent déjà. Rien n'est
                    masqué ni grisé : l'opération reste sélectionnable. */}
                {autres.length > 0 && (
                  <Text style={s.chips} numberOfLines={1}>
                    {autres.map(r => `${nomLigne(r.ligneId)} ${pourcentage(r.montant, o.montant)} %`).join('  ·  ')}
                  </Text>
                )}
                {!on && p.possible && (
                  <Text style={s.apercu}>
                    {t('finances.ceBudget')} : {t('finances.partPrise', { pct: pourcentage(p.montant, o.montant) })}
                    {p.ajustements.length > 0 ? `  ·  ↓ ${p.ajustements.length}` : ''}
                  </Text>
                )}
                {/* §8 — chip mois, visible seulement quand la ligne est cochée. */}
                {on && (
                  <TouchableOpacity onPress={() => setPanneau({ mois: o })} hitSlop={HIT}>
                    <Text style={[s.chipMois, hors && !moisChoisi[o.id] && s.chipMoisAlerte]}>
                      {hors && !moisChoisi[o.id] ? `⚠️ ${t('finances.choisirMois')}` : `📅 ${moisDe(o)}`}
                    </Text>
                  </TouchableOpacity>
                )}
              </View>
              <Text style={[s.opMontant, o.sens === 'revenu' && { color: UI.ok || '#2e7d32' }]}>
                {montantOperation(o.montant, o.sens)}
              </Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      <FeuilleFiltresRattachement visible={panneau === 'filtres'} filtres={filtres}
        sensDefaut={sensDefaut} comptes={comptes} reference={reference}
        onAppliquer={setFiltres} onFermer={() => setPanneau(null)} />

      <CalqueChoix visible={panneau === 'tri'} titre={t('finances.tri')} emoji="↕"
        onFermer={() => setPanneau(null)}>
        {TRIS_RATTACHEMENT.map(x => (
          <TouchableOpacity key={x.id} style={[s.item, tri.tri === x.id && s.itemOn]} activeOpacity={0.7}
            onPress={() => {
              setTri(p => (p.tri === x.id ? { tri: x.id, sens: -p.sens } : { tri: x.id, sens: -1 }));
              setPanneau(null);
            }}>
            <Text style={s.coche}>{tri.tri === x.id ? (tri.sens < 0 ? '↓' : '↑') : '○'}</Text>
            <Text style={s.itemTxt}>{t(x.cle)}</Text>
          </TouchableOpacity>
        ))}
      </CalqueChoix>

      {/* Choix du mois d'affectation : les échéances de la prévision. */}
      <CalqueChoix visible={!!panneau?.mois} titre={t('finances.moisAffectation')} emoji="📅"
        onFermer={() => setPanneau(null)}>
        {moisDus.map(mk => (
          <TouchableOpacity key={mk} style={[s.item, moisDe(panneau?.mois || {}) === mk && s.itemOn]}
            activeOpacity={0.7}
            onPress={() => {
              setMoisChoisi(p => ({ ...p, [panneau.mois.id]: mk }));
              setPanneau(null);
            }}>
            <Text style={s.coche}>{moisDe(panneau?.mois || {}) === mk ? '◉' : '○'}</Text>
            <Text style={s.itemTxt}>{mk}</Text>
          </TouchableOpacity>
        ))}
        <TouchableOpacity style={s.reinit} activeOpacity={0.7}
          onPress={() => {
            setMoisChoisi(p => { const n = { ...p }; delete n[panneau.mois.id]; return n; });
            setPanneau(null);
          }}>
          <Text style={s.reinitTxt}>↺ {t('finances.reinitMoisOp')}</Text>
        </TouchableOpacity>
      </CalqueChoix>
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  // `sansScroll` supprime le `padding: 16` du corps de la Feuille : les blocs
  // de premier niveau portent désormais leur propre marge horizontale.
  entete: { fontSize: 13, color: UI.muted, fontWeight: '700', marginBottom: 10, marginHorizontal: 16, marginTop: 16 },
  somme: { color: UI.ink, fontWeight: '800' },
  barre: { marginHorizontal: 16 },
  btnIc: { width: 42, height: 42, borderRadius: 11, alignItems: 'center', justifyContent: 'center', borderWidth: 0.5, borderColor: UI.border, backgroundColor: UI.card },
  btnIcOn: { borderColor: UI.accent, borderWidth: 1.5 },
  ic: { fontSize: 17 },
  pastille: { position: 'absolute', top: -4, right: -4, minWidth: 17, height: 17, borderRadius: 999, paddingHorizontal: 4, backgroundColor: UI.accent, alignItems: 'center', justifyContent: 'center' },
  pastilleTxt: { fontSize: 10.5, color: '#fff', fontWeight: '800' },
  rechercheZone: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: UI.card, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, paddingHorizontal: 12 },
  recherche: { flex: 1, paddingVertical: 10, fontSize: 14, color: UI.ink },
  op: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, backgroundColor: UI.card, borderRadius: 12, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 10, paddingHorizontal: 12, marginBottom: 8 },
  opOn: { borderColor: UI.accent, borderWidth: 1.5 },
  coche: { fontSize: 16, color: UI.accent, width: 20, marginTop: 1 },
  opLib: { fontSize: 14, fontWeight: '700', color: UI.ink },
  opLibOn: { fontWeight: '800' },
  opSous: { fontSize: 11.5, color: UI.muted, marginTop: 1 },
  chips: { fontSize: 11, color: UI.faint, marginTop: 3, fontWeight: '700' },
  apercu: { fontSize: 11, color: UI.accent, marginTop: 3, fontWeight: '700' },
  chipMois: { fontSize: 11.5, color: UI.accent, marginTop: 5, fontWeight: '800' },
  chipMoisAlerte: { color: '#c47f00' },
  opMontant: { fontSize: 14, fontWeight: '800', color: UI.ink },
  vide: { alignItems: 'center', paddingVertical: 26, gap: 10 },
  videTxt: { fontSize: 13, color: UI.faint, textAlign: 'center' },
  videLien: { fontSize: 13, color: UI.accent, fontWeight: '800' },
  item: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 12, paddingHorizontal: 12, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, backgroundColor: UI.card, marginBottom: 8 },
  itemOn: { borderColor: UI.accent },
  itemTxt: { flex: 1, fontSize: 14.5, color: UI.ink },
  reinit: { alignItems: 'center', paddingVertical: 12, marginTop: 10, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border },
  reinitTxt: { fontSize: 13.5, fontWeight: '700', color: UI.muted },
});
